import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { useFlexLayout, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';
import { Button } from 'ui-components/components/button';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import Global from '../../../../store/global-selectors';
import FormattedCurrencyContainer from '../../../../components/formatted-currency/container';
import { openProductDetailModal } from '../../../../store/products/products.actions';
import { USD } from '../../../../constants/currency-codes';
import { emptyArray } from 'ui-components/default-props/empty';
import commonMessages from '../common-messages';

import style from '../common-styles.module.less';
import analyticsStyle from '../../analytics-style.module.less';

const TITLE = 'title';
const PRODUCT = 'product';
const REVENUE = 'revenue';
const QUANTITY = 'quantity';

const revenueMessages = defineMessages({
  [TITLE]: {
    defaultMessage: 'Top 5 Products by Revenue',
    id: 'bUu5a+',
    description: 'Top Five Products by Revenue table title',
  },
  [PRODUCT]: {
    defaultMessage: 'Product',
    id: 'EYTQxr',
    description: 'Top Five Products Table product header',
  },
  [REVENUE]: {
    defaultMessage: 'Revenue',
    id: '+7FsOQ',
    description: 'Top Five Products by Revenue revenue header',
  },
});

const quantityMessages = defineMessages({
  [TITLE]: {
    defaultMessage: 'Top 5 Products by Quantity',
    id: 'VzGOar',
    description: 'Top Five Products by Quantity table title',
  },
  [PRODUCT]: {
    defaultMessage: 'Product',
    id: 'EYTQxr',
    description: 'Top Five Products Table product header',
  },
  [QUANTITY]: {
    defaultMessage: 'Qty',
    id: 'vI91wJ',
    description: 'Top Five Products by Quantity abbreviated header',
  },
});

type ProductRow = {
  product: React.ReactNode;
  revenue: React.ReactNode;
  quantity: string;
};

type ProductData = {
  name: string;
  amount: number;
  count: number;
  imageUrl?: string;
  productId?: number;
};

type TopFiveProductsByRevenueTableProps = {
  data?: ProductData[];
  byQuantity?: boolean;
};

export const TopFiveProductsByRevenueTable = ({
  data = emptyArray as ProductData[],
  byQuantity = false,
}: TopFiveProductsByRevenueTableProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const openModal = useCallback((id: number) => dispatch(openProductDetailModal(id)), [dispatch]);
  const defaultCurrency = useSelector(Global.selectSellerDefaultCurrency) || USD;
  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const msgs = byQuantity ? quantityMessages : revenueMessages;
  const tableTitle = useMemo(() => intl.formatMessage(msgs[TITLE]), [intl, msgs]);

  const emptyRow = useMemo(() => (!data.length ? <FormattedMessage {...commonMessages.nullStateMessage} /> : null), [data.length]);

  const columns = useMemo(() => {
    const productColumn = {
      Header: () => {
        const title = intl.formatMessage(msgs[PRODUCT]);
        return isShipStationAnalyticsFeatureEnabled ? (
          <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{title}</div>
        ) : (
          title
        );
      },
      accessor: PRODUCT as typeof PRODUCT,
      Cell: ({ value }: { value: React.ReactNode }) =>
        isShipStationAnalyticsFeatureEnabled ? (
          <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{value}</div>
        ) : (
          value
        ),
    };

    if (byQuantity) {
      return [
        productColumn,
        {
          Header: () => {
            const title = intl.formatMessage(quantityMessages[QUANTITY]);
            return (
              <div
                className={classnames(style.right, {
                  [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
                })}
              >
                {title}
              </div>
            );
          },
          accessor: QUANTITY as typeof QUANTITY,
          Cell: ({ value }: { value: string }) => (
            <div
              className={classnames(style.right, {
                [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
              })}
            >
              {value}
            </div>
          ),
        },
      ];
    }

    return [
      productColumn,
      {
        Header: () => {
          const title = intl.formatMessage(revenueMessages[REVENUE]);
          return (
            <div
              className={classnames(style.right, {
                [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
              })}
            >
              {title}
            </div>
          );
        },
        accessor: REVENUE as typeof REVENUE,
        Cell: ({ value }: { value: React.ReactNode }) => (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {value} {defaultCurrency}
          </div>
        ),
      },
    ];
  }, [intl, msgs, byQuantity, isShipStationAnalyticsFeatureEnabled, defaultCurrency]);

  const tableData = useMemo(() => {
    return data.map((product) => ({
      product: product.productId ? (
        <Button onClick={() => openModal(product.productId!)} theme='link'>
          {product.name}
        </Button>
      ) : (
        product.name
      ),
      revenue: <FormattedCurrencyContainer value={product.amount} />,
      quantity: intl.formatNumber(product.count),
    }));
  }, [data, intl, openModal]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<ProductRow>(
    {
      columns,
      data: tableData,
    },
    useFlexLayout,
  );

  return (
    <>
      <div
        className={classnames(
          { [style['table-title']]: !isShipStationAnalyticsFeatureEnabled },
          { [analyticsStyle['analytics-table-title']]: isShipStationAnalyticsFeatureEnabled },
        )}
      >
        <Text textType={TextType.H3} value={tableTitle} />
      </div>
      <ReactTableWrapper
        className={classnames({
          [analyticsStyle['analytics-table']]: isShipStationAnalyticsFeatureEnabled,
        })}
        emptyRow={emptyRow}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    </>
  );
};
